// Renders inputs for the per-type secret fields. Secrets are write-only
// from the browser (the api redacts them on GET); leaving a field empty
// means "don't change" on PATCH. On POST all required fields must be
// filled.
//
// Show/hide: optional toggle (default hidden) per field. We never
// display a "current value" placeholder because the api doesn't give
// us one — and inventing one would create a false sense of security.

import { useState } from 'react';
import { getIngestorSchema } from './schema';

export function SecretForm({
  type,
  value,
  onChange,
  required = false,
}: {
  type: string;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  required?: boolean; // true on create; false on edit (empty = "don't change")
}) {
  const schema = getIngestorSchema(type);

  if (!schema) {
    return (
      <p className="text-xs text-ink-dim">No secret schema registered for this type.</p>
    );
  }
  if (schema.secrets.length === 0) {
    return <p className="text-xs text-ink-dim">This ingestor type takes no secrets.</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-dim">
        Secrets are stored encrypted at rest. {required ? 'All required fields must be set.' : 'Leave a field empty to keep the current value.'}
      </p>
      {schema.secrets.map((f) => (
        <SecretField
          key={f.name}
          id={`sec-${f.name}`}
          field={f}
          required={required && f.required !== false}
          value={value[f.name] ?? ''}
          onChange={(v) => onChange({ ...value, [f.name]: v })}
        />
      ))}
    </div>
  );
}

function SecretField({
  id,
  field,
  required,
  value,
  onChange,
}: {
  id: string;
  field: { name: string; label: string; help?: string; required?: boolean };
  required: boolean;
  value: string;
  onChange: (v: string) => void;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="space-y-1">
      <label className="label" htmlFor={id}>
        {field.label}
        {required ? <span className="ml-1 text-accent-danger">*</span> : null}
      </label>
      <div className="flex gap-2">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          className="input flex-1 font-mono"
          autoComplete="off"
          spellCheck={false}
          required={required}
          value={value}
          placeholder={required ? '' : '•••••• (unchanged)'}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="btn"
          aria-pressed={shown}
          onClick={() => setShown(!shown)}
        >
          {shown ? 'Hide' : 'Show'}
        </button>
      </div>
      {field.help ? <p className="text-xs text-ink-dim">{field.help}</p> : null}
    </div>
  );
}
